import { useState, useCallback } from 'react';
import { ViewsAPI } from '../utils/api';
import { isRetiredViewId } from '../components/SavedViews/types';
import { showToast } from '../components/Toast';
import { notifyInboxTargetsGone } from '../utils/notificationInbox';

/**
 * useAppNavigation Hook
 * 
 * Manages navigation state and handlers for switching between
 * sprints, templates, and saved views.
 */
export function useAppNavigation({ loadFullSprint, loadFullTemplate, setLastSelectedCampaignId, VIEWS }) {
  const [currentView, setCurrentView] = useState(VIEWS.EMPTY);
  const [selectedSprintId, setSelectedSprintId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  
  // Preferred view for SprintDetail (flow, kanban, table) - set when navigating from search
  const [preferredDetailView, setPreferredDetailView] = useState(null);
  // Mission to focus on after navigation
  const [pendingMissionFocus, setPendingMissionFocus] = useState(null);
  
  // Saved Views state - when a view is active, we show multi-campaign data
  const [activeView, setActiveView] = useState(null);
  
  // Loading state for when switching between campaigns/templates
  const [isLoadingItem, setIsLoadingItem] = useState(false);

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  // Minimum time to show loading (allows animation to play smoothly)
  const MIN_LOADING_TIME = 300; // ms

  const isTemplateItem = (item) => item?.isTemplate === true || item?.type === 'template';

  const handleSelectTemplate = useCallback(async (template, options = {}) => {
    const { view = null, missionId = null } = options;

    const isNewTemplate = template.id !== selectedTemplateId;

    if (isNewTemplate) {
      setIsLoadingItem(true);
    }

    let fullTemplate = template;
    if (!Array.isArray(template.tasks)) {
      fullTemplate = await loadFullTemplate(template.id);
      if (!fullTemplate) {
        console.error('[handleSelectTemplate] Failed to load template:', template.id);
        setIsLoadingItem(false);
        return;
      }
    }

    // Templates have their own views. A campaign's customViewId won't match and
    // useInitialNavigation falls back to view type.
    if (view) {
      setPreferredDetailView(view);
    }

    setSelectedTemplateId(fullTemplate.id);
    setSelectedSprintId(null);
    setCurrentView(VIEWS.TEMPLATE_DETAIL);
    setActiveView(null);

    if (missionId) {
      setPendingMissionFocus(missionId);
    }

    if (isNewTemplate) {
      await new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME));
      setIsLoadingItem(false);
    }
  }, [selectedTemplateId, loadFullTemplate, VIEWS]);

  const handleSelectSprint = useCallback(async (sprint, options = {}) => {
    // Breadcrumb back to template root calls onSelectSprint(template) — route correctly
    if (isTemplateItem(sprint)) {
      return handleSelectTemplate(sprint, options);
    }

    const { view = null, missionId = null } = options;
    
    // Show loading immediately (optimistic) when switching to a different campaign
    const isNewCampaign = sprint.id !== selectedSprintId;
    
    if (isNewCampaign) {
      setIsLoadingItem(true);
    }
    
    // Load full sprint data FIRST if needed, before setting selectedSprintId
    // This prevents the flash of "no tasks" state.
    // Use Array.isArray — empty [] is truthy and must not skip the fetch.
    let fullSprint = sprint;
    if (!Array.isArray(sprint.tasks)) {
      fullSprint = await loadFullSprint(sprint.id);
      if (!fullSprint || !Array.isArray(fullSprint.tasks)) {
        console.error('[handleSelectSprint] Failed to load sprint:', sprint.id, fullSprint);
        if (missionId) {
          showToast('This campaign no longer exists', { type: 'info' });
          notifyInboxTargetsGone({ campaignIds: [sprint.id] });
        }
        setIsLoadingItem(false);
        return;
      }
    }
    
    // Preferred view first so SprintDetail never paints the new campaign with a stale/null
    // initialView before the intended mode is applied (avoids a one-frame flash to flow).
    if (view) {
      setPreferredDetailView(view);
    }

    // Now set the selected sprint - it's guaranteed to have tasks
    setSelectedSprintId(fullSprint.id);
    setSelectedTemplateId(null);
    setCurrentView(VIEWS.SPRINT_DETAIL);
    
    // Save as last selected campaign for next session
    setLastSelectedCampaignId(fullSprint.id);
    
    // Clear any active saved view when selecting a specific campaign
    setActiveView(null);
    
    // Set pending mission focus if specified
    if (missionId) {
      console.log('[useAppNavigation] Setting pendingMissionFocus:', missionId);
      setPendingMissionFocus(missionId);
    }
    
    // Minimum loading time for smooth animation
    if (isNewCampaign) {
      await new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME));
      setIsLoadingItem(false);
    }
  }, [selectedSprintId, loadFullSprint, setLastSelectedCampaignId, handleSelectTemplate, VIEWS]);

  const handleSelectView = useCallback(async (view) => {
    if (view && isRetiredViewId(view.id)) {
      setActiveView(null);
      setCurrentView(VIEWS.EMPTY);
      return;
    }

    if (view) {
      // If only an ID is provided (e.g., from URL navigation), fetch the full view data
      let fullView = view;
      if (view.id && !view.query) {
        try {
          console.log('[handleSelectView] Fetching full view data for:', view.id);
          fullView = await ViewsAPI.get(view.id);
          if (!fullView) {
            console.error('[handleSelectView] View not found:', view.id);
            return;
          }
        } catch (error) {
          console.error('[handleSelectView] Failed to fetch view:', error);
          return;
        }
      }
      
      setActiveView(fullView);
      // Clear single-campaign selection when a view is active
      setSelectedSprintId(null);
      setSelectedTemplateId(null);
      setCurrentView(VIEWS.SPRINT_DETAIL); // Use sprint detail view for rendering
    } else {
      // Clearing the active view - show empty state
      setActiveView(null);
      setCurrentView(VIEWS.EMPTY);
    }
  }, [VIEWS]);

  // Update the active view (for optimistic updates when renaming/changing icon)
  const updateActiveView = useCallback((updates) => {
    setActiveView(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  // Settings is a gear overlay — it must never take over the main view.
  // Leftover callers (legacy /settings URL) are handled by openSettingsModal.
  const handleSidebarNavigate = useCallback((_viewId) => {}, []);

  const clearPreferredDetailView = useCallback(() => {
    setPreferredDetailView(null);
  }, []);

  const clearPendingMissionFocus = useCallback(() => {
    setPendingMissionFocus(null);
  }, []);

  const clearActiveView = useCallback(() => {
    setActiveView(null);
  }, []);

  return {
    // State
    currentView,
    setCurrentView,
    selectedSprintId,
    setSelectedSprintId,
    selectedTemplateId,
    setSelectedTemplateId,
    preferredDetailView,
    setPreferredDetailView,
    pendingMissionFocus,
    setPendingMissionFocus,
    activeView,
    isLoadingItem,
    setIsLoadingItem,
    
    // Handlers
    handleSelectSprint,
    handleSelectView,
    updateActiveView,
    clearActiveView,
    handleSelectTemplate,
    handleSidebarNavigate,
    clearPreferredDetailView,
    clearPendingMissionFocus,
  };
}

export default useAppNavigation;
